import React, {useEffect, useRef, useState} from "react";
import {
    AccountingAccountList,
    AccountingAccountRestControllerApiFactory,
    ScanSupplierBillRequest,
    SupplierBill,
    SupplierBillLine,
    SupplierBillRestControllerApiFactory,
    SupplierBillScanResultDto,
} from "arteco-api-client-ts";
import {fmtAmt, fmtAccountCode} from "../../../../components/utils/numbers";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {Add, Delete, DocumentScanner} from "@mui/icons-material";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {types} from "../../../../components/form/types";
import {useLocation, useNavigate} from "react-router-dom";
import {SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {numSx} from "../tax_submission/labels";
import {postableAccounts, renderAccountOption} from "../../../../components/utils/accounts";

/**
 * Vencimiento propuesto al dar de alta: emisión + 30 días.
 *
 * Es una propuesta editable, no un dato inventado: el campo es obligatorio (#11707) y dejarlo en
 * blanco obligaría a teclearlo siempre, que es como se llega a 56 facturas sin vencimiento.
 */
function defaultDueDate(issueDate?: string): string {
    const base = issueDate ? new Date(issueDate) : new Date();
    base.setDate(base.getDate() + 30);
    return base.toISOString().split("T")[0];
}

// El escaneo navega a esta misma ruta con el resultado en `location.state`. Sin `key`, React
// reutiliza el componente, `useForm` no vuelve a ejecutar su `factory` y el formulario se queda
// vacío y en «Escaneando...» (#13466). Con `key={location.key}` se remonta en cada navegación.
export const SupplierBillEditPage = () => {
    const location = useLocation();
    return <SupplierBillEditForm key={location.key}/>;
};

const SupplierBillEditForm = () => {
    const apiManager = useApiManager();
    const ctrl = apiManager.factory(SupplierBillRestControllerApiFactory);
    const location = useLocation();
    const navigate = useNavigate();
    const scanResult = location.state?.scan as SupplierBillScanResultDto | undefined;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [lineAccount, setLineAccount] = useState<AccountingAccountList | null>(null);
    const [lineAmount, setLineAmount] = useState("");
    const [lineDesc, setLineDesc] = useState("");
    const [accountOptions, setAccountOptions] = useState<AccountingAccountList[]>([]);
    const [accountInput, setAccountInput] = useState("");
    const [aiEnabled, setAiEnabled] = useState(false);
    const [scanning, setScanning] = useState(false);

    useEffect(() => {
        apiManager.execute(ctrl.scanStatusSupplierBill())
            .then(data => setAiEnabled(data['enabled'] ?? false))
            .catch(() => {});
    }, []);

    const handleAccountSearch = (term: string) => {
        if (term.length < 1) return;
        const accountCtrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
        apiManager.execute(accountCtrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, undefined, term, false))
            .then((list) => setAccountOptions(postableAccounts(list as any)));
    };

    const handleScanPdf = (file: File) => {
        setScanning(true);
        // Multipart, como bank_statement y contasol: el cliente generado lo manda en JSON y el
        // endpoint (@RequestParam MultipartFile) responde 500 «not a multipart request» (#13466).
        const fd = new FormData();
        fd.append('pdf', file);
        apiManager.execute(ctrl.scanSupplierBill(fd as unknown as ScanSupplierBillRequest, {headers: {'Content-Type': null as any}}))
            .then(data => {
                navigate("/app/admin/accounting/supplier-bill/new", {state: {scan: data}});
            })
            .catch(() => {
                setScanning(false);
            });
    };

    const form = useForm({
        fetcher: (id) => ctrl.getSupplierBill(id),
        updater: (bean) => ctrl.saveSupplierBill(bean),
        remover: (id) => ctrl.deleteOrDismissSupplierBill(id),
        factory: () => ({
            issueDate: scanResult?.issueDate ?? new Date().toISOString().split("T")[0],
            dueDate: defaultDueDate(scanResult?.issueDate),
            billNumber: scanResult?.billNumber,
            baseAmount: scanResult?.baseAmount,
            vatRate: scanResult?.vatRate ?? 21,
            totalAmount: scanResult?.totalAmount,
            rentalWithholding: scanResult?.withheldAmount != null && scanResult.withheldAmount > 0,
            withheldAmount: scanResult?.withheldAmount ?? undefined,
            lines: [],
        } as unknown as SupplierBill),
        postLoad: (bean) => {
            if (!bean.id && scanResult?.supplierId) {
                form.triggerField("supplier", {
                    id: scanResult.supplierId,
                    name: scanResult.supplierDisplayName,
                });
            }
        },
    } as UseFormProps<SupplierBill>);

    const bean = form.propModel.value;
    const lines = (bean?.lines ?? []) as SupplierBillLine[];

    const handleGenerateAccounting = () => {
        apiManager.execute(
            ctrl.generateAccountingSupplierBill(form.getIdOrErr()),
            "Asiento contable generado"
        ).then(journalEntry => {
            form.setter({...bean!, journalEntry} as SupplierBill);
        });
    };

    const reloadBill = () => {
        const billId = form.getIdOrErr();
        apiManager.execute(ctrl.getSupplierBill(billId)).then((b) => form.setter(b));
    };

    const handleAddLine = () => {
        if (!lineDesc || !lineAmount) return;
        const billId = form.getIdOrErr();
        const line = {
            description: lineDesc,
            amount: parseFloat(lineAmount),
            ...(lineAccount ? {expenseAccount: lineAccount} : {}),
        } as unknown as SupplierBillLine;
        apiManager.execute(ctrl.saveLineSupplierBill(billId, line), "Línea añadida").then(() => {
            reloadBill();
            setDialogOpen(false);
            setLineAccount(null);
            setLineAmount("");
            setLineDesc("");
            setAccountInput("");
        });
    };

    const handleDeleteLine = (lineId: number) => {
        apiManager.execute(ctrl.deleteLineSupplierBill(lineId)).then(() => reloadBill());
    };

    const totalLines = lines.reduce((s, l) => s + (l.amount ?? 0), 0);

    return (
        <FormContent form={form} title={"Factura recibida"}>
            {aiEnabled && !form.getId() && (
                <Box mb={3} sx={{
                    p: 2, borderRadius: 2.5, border: "1px dashed", borderColor: "divider",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap",
                }}>
                    <Typography variant="body2" color="text.secondary">
                        ¿Tienes el PDF? Escanéalo y se rellenan el proveedor, las fechas y los importes; luego revísalos.
                    </Typography>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="application/pdf"
                        style={{display: "none"}}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleScanPdf(file);
                        }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={scanning ? <CircularProgress size={16}/> : <DocumentScanner/>}
                        disabled={scanning}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {scanning ? "Escaneando..." : "Escanear factura PDF"}
                    </Button>
                </Box>
            )}

            <FormAutocomplete
                field={"supplier"}
                label={"Proveedor"}
                type={types.suppliers}
            />

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"billNumber"} label={"Número de factura"}/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"issueDate"} label={"Fecha emisión"} type="date"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    {/* Obligatorio en las altas (#11707): sin vencimiento, la alerta de facturas
                        vencidas no puede disparar y no hay previsión de pagos. El backend lo
                        rechaza; aquí se marca para que no haya que descubrirlo al guardar. */}
                    <FormInput field={"dueDate"} label={"Fecha vencimiento *"} type="date"/>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"baseAmount"} label={"Base imponible"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"vatRate"} label={"% IVA"} type="number"/>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FormInput field={"totalAmount"} label={"Total"} type="number" disabled/>
                </Grid>
            </Grid>

            <FormInput field={"description"} label={"Descripción"} rows={3}/>
            <FormInput field={"comments"} label={"Comentarios"} rows={2}/>

            <Box mt={1}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!!bean?.rentalWithholding}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                const suggested = checked && bean?.baseAmount
                                    ? parseFloat((bean.baseAmount * 0.19).toFixed(4))
                                    : undefined;
                                form.setter({
                                    ...bean!,
                                    rentalWithholding: checked,
                                    withheldAmount: checked ? (suggested ?? bean?.withheldAmount) : undefined,
                                } as SupplierBill);
                            }}
                        />
                    }
                    label="Factura de alquiler (retención 19%)"
                />
                {bean?.rentalWithholding && (
                    <Box mt={1} maxWidth={300}>
                        <TextField
                            label="Importe retenido (€)"
                            type="number"
                            variant="filled"
                            fullWidth
                            value={bean?.withheldAmount ?? ""}
                            onChange={(e) => {
                                const val = e.target.value === "" ? undefined : parseFloat(e.target.value);
                                form.setter({...bean!, withheldAmount: val} as SupplierBill);
                            }}
                            helperText={bean?.baseAmount
                                ? `19% de ${fmtAmt(bean.baseAmount)} = ${fmtAmt(bean.baseAmount * 0.19)} €`
                                : "Introduce la base imponible para ver la sugerencia"}
                        />
                    </Box>
                )}
            </Box>

            {form.getId() && (
                <Box display="flex" alignItems="center" gap={2} mt={1}>
                    {bean?.journalEntry ? (
                        <SoftChip tone="success"
                                  label={`Contabilizada · asiento nº ${bean.journalEntry.entryNumber ?? "borrador"}`}/>
                    ) : (
                        <Button variant="outlined" onClick={handleGenerateAccounting}>
                            Contabilizar factura
                        </Button>
                    )}
                </Box>
            )}

            {form.getId() && (
                <Box mt={3}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight={700}>Líneas de la factura</Typography>
                        <Button
                            startIcon={<Add/>}
                            variant="outlined"
                            size="small"
                            onClick={() => setDialogOpen(true)}
                        >
                            Añadir línea
                        </Button>
                    </Box>

                    <TableContainer sx={tableContainerSx}>
                    <Table size="small" sx={tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Descripción</TableCell>
                                <TableCell>Cuenta de gasto</TableCell>
                                <TableCell align="right">Importe</TableCell>
                                <TableCell width={40}/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line) => (
                                <TableRow key={line.id}>
                                    <TableCell>{line.description}</TableCell>
                                    <TableCell>
                                        {line.expenseAccount
                                            ? <><Box component="span" sx={numSx}>{line.expenseAccount.code}</Box> · {line.expenseAccount.name}</>
                                            : <Box component="span" sx={{color: "text.disabled"}}>Sin cuenta</Box>}
                                    </TableCell>
                                    <TableCell align="right" sx={numSx}>{fmtAmt(line.amount)}</TableCell>
                                    <TableCell>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDeleteLine(line.id!)}
                                        >
                                            <Delete fontSize="small"/>
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow sx={{"& td": {bgcolor: "grey.50", fontWeight: 700}}}>
                                <TableCell colSpan={2}>Total líneas</TableCell>
                                <TableCell align="right" sx={numSx}>{fmtAmt(totalLines)}</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableBody>
                    </Table>
                    </TableContainer>

                    {lines.length > 0 && bean?.baseAmount != null &&
                        Math.abs(totalLines - bean.baseAmount) > 0.01 && (
                            <Alert severity="warning" sx={{mt: 1}}>
                                Las líneas ({fmtAmt(totalLines)}) no coinciden con la base
                                imponible ({fmtAmt(bean.baseAmount)}).
                                Diferencia: {fmtAmt(Math.abs(totalLines - bean.baseAmount))} €
                            </Alert>
                        )}
                </Box>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
                <DialogTitle sx={{fontWeight: 700}}>Añadir línea</DialogTitle>
                <DialogContent>
                    <Box sx={{pt: 1, display: "flex", flexDirection: "column", gap: 2}}>
                        <TextField
                            label="Descripción"
                            value={lineDesc}
                            onChange={(e) => setLineDesc(e.target.value)}
                            variant="filled"
                            fullWidth
                            required
                        />
                        <TextField
                            label="Importe"
                            type="number"
                            value={lineAmount}
                            onChange={(e) => setLineAmount(e.target.value)}
                            variant="filled"
                            fullWidth
                            required
                        />
                        <Autocomplete
                            options={accountOptions}
                            value={lineAccount}
                            inputValue={accountInput}
                            onInputChange={(_, val) => {
                                setAccountInput(val);
                                handleAccountSearch(val);
                            }}
                            onChange={(_, val) => setLineAccount(val)}
                            getOptionLabel={(o) => `${fmtAccountCode(o.code)} - ${o.name}`}
                            isOptionEqualToValue={(a, b) => a.id === b.id}
                            renderOption={(props, option) => renderAccountOption(props, option as any)}
                            renderInput={(params) => (
                                <TextField {...params} label="Cuenta de gasto (opcional)" variant="filled" fullWidth/>
                            )}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{px: 3, pb: 2.5}}>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        onClick={handleAddLine}
                        disabled={!lineDesc || !lineAmount}
                    >
                        Añadir
                    </Button>
                </DialogActions>
            </Dialog>
        </FormContent>
    );
};
